import { isEmptyString } from '@/utils/string';

/**
 * What the events endpoint will accept.
 *
 * The limits are the backend's, and the disallowed names are the ones the
 * transactional sender reads for itself — an event carrying them would be
 * rejected, or worse, quietly reinterpreted.
 */

export const MAX_EVENT_NAME_LENGTH = 100;

/** 10 MB, measured on the JSON as typed. */
export const MAX_EVENT_DATA_SIZE = 10 * 1024 * 1024;

export const DISALLOWED_EVENT_DATA_PROPERTIES = ['attachments', 'from_address', 'recipient', 'reply_to'];

/** "a, b, c or d", as the message reads. */
function orList(items: readonly string[]): string {
  if (items.length < 2) {
    return items.join('');
  }
  return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;
}

const DISALLOWED_PROPERTIES_MESSAGE = `Event data cannot have properties named ${orList(
  DISALLOWED_EVENT_DATA_PROPERTIES,
)}`;

/** Why the event name is not sendable, or nothing when it is. */
export function eventNameError(name: string): string | undefined {
  if (isEmptyString(name)) {
    return 'Enter the event name to send';
  }
  if (name.length > MAX_EVENT_NAME_LENGTH) {
    return `Event name must not be more than ${String(MAX_EVENT_NAME_LENGTH)} characters`;
  }
  return undefined;
}

/**
 * Why the event data is not sendable, or nothing when it is. No data at all
 * is valid: most events carry none.
 */
export function eventDataError(data: string): string | undefined {
  if (isEmptyString(data)) {
    return undefined;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return 'Event data must be valid JSON';
  }

  if (typeof parsed !== 'object' || parsed == null) {
    return 'Event data must be valid JSON object or array';
  }
  if (data.length > MAX_EVENT_DATA_SIZE) {
    return 'Event data must not exceed 10 MB in size';
  }
  if (Object.keys(parsed).some((key) => DISALLOWED_EVENT_DATA_PROPERTIES.includes(key))) {
    return DISALLOWED_PROPERTIES_MESSAGE;
  }
  return undefined;
}
